<template>
    <main class="support-tickets-page">
        <v-container class="support-tickets-page__container">
            <account-settings-header
                :items="links"
                title="Support tickets"
                subtitle="Review support requests, assigned agents, and ticket activity for your account organization."
                :icon="mdiLifebuoy"
            />
            <header class="support-tickets-page__heading">
                <div class="support-tickets-page__heading-copy">
                    <span class="support-tickets-page__eyebrow">YOUR ACCOUNT / HAPPYFOX</span>
                    <h2 id="support-tickets-heading">Ticket history</h2>
                    <p>Organization support tickets · Most recent first</p>
                </div>
                <span v-if="organizationId && !loading && !error" class="support-tickets-page__count">
                    {{ tickets.length }} tickets
                </span>
            </header>

            <section class="support-tickets-card" aria-labelledby="support-tickets-heading">
                <section class="support-tickets-card__filters" aria-label="Filter support tickets">
                    <v-text-field
                        id="support-ticket-search"
                        v-model="search"
                        class="support-tickets-card__filter"
                        :append-icon="mdiMagnify"
                        label="Search by ticket ID, subject or status"
                        type="search"
                        placeholder="Find a support ticket"
                        :disabled="loading || !!error || !organizationId"
                        dark
                        outlined
                        dense
                        single-line
                        hide-details
                        clearable
                        @click:clear="search = ''"
                    />
                </section>

                <div v-if="loading" class="support-tickets-card__state" role="status" aria-live="polite">
                    <v-progress-circular indeterminate color="primary" size="28" aria-hidden="true" />
                    <p>Loading organization support tickets…</p>
                </div>
                <div v-else-if="error" class="support-tickets-card__state" role="alert">
                    <p>{{ error }}</p>
                    <button type="button" class="support-tickets-page__action" @click="fetchTickets">Retry</button>
                </div>
                <div v-else-if="!tickets.length" class="support-tickets-card__state" role="status">
                    <p>{{ emptyText }}</p>
                </div>
                <div v-else class="support-tickets-card__table-wrap">
                    <v-data-table
                        class="support-tickets-table"
                        dark
                        :headers="headers"
                        :items="filteredTickets"
                        :page.sync="page"
                        :items-per-page="10"
                        :mobile-breakpoint="0"
                        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
                        item-key="id"
                        no-data-text="No tickets match your search."
                        no-results-text="No tickets match your search."
                    >
                        <template v-slot:[`item.ticketId`]="{ item }">
                            <button type="button" class="support-tickets-table__open" @click="selectTicket(item.id)">
                                {{ text(item.ticketId) }}
                            </button>
                        </template>
                        <template v-slot:[`item.subject`]="{ item }">
                            <span class="support-tickets-table__subject" :title="text(item.subject, 'Untitled ticket')">
                                {{ text(item.subject, 'Untitled ticket') }}
                            </span>
                        </template>
                        <template v-slot:[`item.status`]="{ item }">
                            <span class="support-tickets-table__status" :class="`is-${statusTone(item.status)}`">
                                {{ text(item.status, 'Unknown') }}
                            </span>
                        </template>
                        <template v-slot:[`item.createdat`]="{ item }">
                            {{ formatDate(item.createdat) }}
                        </template>
                        <template v-slot:[`item.create_by`]="{ item }">
                            <span>{{ text(item.create_by) }}</span>
                            <span class="support-tickets-table__email">{{ text(item.create_by_email, '') }}</span>
                        </template>
                    </v-data-table>
                </div>
            </section>
            <happyfox-ticket-details-dialog
                v-if="selectedTicket"
                :value="dialog"
                :ticket="selectedTicket"
                @input="!$event && closeDialog()"
            />
        </v-container>
    </main>
</template>

<script>
import Vue from 'vue'
import gql from 'graphql-tag'
import { mdiLifebuoy, mdiMagnify } from '@mdi/js'
import { hfTicketsByCustomerId } from '@/api/hf_tickets'
import {
    ticketText,
    ticketTimestamp,
    formatTicketDate,
    ticketStatusTone,
    normalizeTickets
} from '@/utils/happyfox_tickets'
import HappyfoxTicketDetailsDialog from '@/views/account_dashboard/components/happyfox_ticket_details_dialog.vue'
import AccountSettingsHeader from '../account_settings_header.vue'
import { navLinks } from '../navigation'

/** @typedef {{ id: string, ticketId?: string | number, subject?: string, status?: string,
 * createdat?: string | { value: string } }} SupportTicket */

const BASE_ORGANIZATION_QUERY = gql`
    query ResolveBaseAccountOrganization {
        resolveBaseAccountOrganization {
            orgId
        }
    }
`

export default Vue.extend({
    name: 'SupportTickets',
    components: { AccountSettingsHeader, HappyfoxTicketDetailsDialog },
    metaInfo: { title: 'Your Account', subTitle: 'Support tickets' },
    data() {
        return {
            mdiLifebuoy,
            mdiMagnify,
            links: navLinks,
            /** @type {SupportTicket[]} */
            tickets: [],
            organizationId: null,
            loading: false,
            error: '',
            requestId: 0,
            activeContext: null,
            search: '',
            page: 1,
            dialog: false,
            selectedTicket: null,
            headers: [
                { text: 'Ticket ID', value: 'ticketId', sortable: false },
                { text: 'Subject', value: 'subject', sortable: false },
                { text: 'Status', value: 'status', sortable: false },
                { text: 'Created', value: 'createdat', sortable: false },
                { text: 'Requester', value: 'create_by', sortable: false }
            ]
        }
    },
    computed: {
        authReady() {
            return !!this.$auth && !this.$auth.loading && this.$auth.isAuthenticated === true && !!this.$auth.user?.id
        },
        isMember() {
            return (
                this.authReady && typeof this.$auth.userInOrganization === 'function' && this.$auth.userInOrganization()
            )
        },
        authContext() {
            const user = this.$auth?.user
            return JSON.stringify([this.authReady, this.isMember, user?.id, user?.org?.branchId, user?.org?.name])
        },
        /** @returns {SupportTicket[]} */
        sortedTickets() {
            return [...this.tickets].sort((a, b) => {
                const first = ticketTimestamp(a.createdat)
                const second = ticketTimestamp(b.createdat)
                if (first === null) return second === null ? 0 : 1
                return second === null ? -1 : second - first
            })
        },
        /** @returns {SupportTicket[]} */
        filteredTickets() {
            const query = (this.search || '').trim().toLowerCase()
            return this.sortedTickets.filter((ticket) =>
                [ticket.ticketId, ticket.subject, ticket.status].some((value) =>
                    ticketText(value, '').toLowerCase().includes(query)
                )
            )
        },
        emptyText() {
            if (!this.authReady) return 'Sign in to view support tickets for your account organization.'
            if (!this.isMember) {
                return 'Your account is not linked to an organization. Ask your administrator for an invitation, or contact Getac Support.'
            }
            return this.organizationId
                ? 'Your organization has no support tickets yet.'
                : 'No organization was found for your account. Contact your organization administrator or Getac Support.'
        }
    },
    watch: {
        authContext: {
            immediate: true,
            handler() {
                this.fetchTickets()
            }
        },
        search() {
            this.page = 1
        }
    },
    beforeDestroy() {
        this.requestId++
    },
    methods: {
        text: ticketText,
        formatDate: formatTicketDate,
        statusTone: ticketStatusTone,
        async fetchTickets() {
            const context = this.authContext
            if (this.activeContext === context) return
            const requestId = ++this.requestId
            this.activeContext = null
            this.tickets = []
            this.organizationId = null
            this.error = ''
            this.search = ''
            this.page = 1
            this.closeDialog()
            this.loading = !!this.$auth?.loading
            if (!this.isMember) return
            this.activeContext = context
            this.loading = true
            const isCurrent = () => requestId === this.requestId && context === this.authContext
            try {
                const { data } = await this.$apollo.query({
                    query: BASE_ORGANIZATION_QUERY,
                    fetchPolicy: 'network-only'
                })
                if (!isCurrent()) return
                const base = data?.resolveBaseAccountOrganization
                if (base === null) return
                if (typeof base?.orgId !== 'string' || !base.orgId.trim()) {
                    throw new Error('Base organization unavailable')
                }
                const response = await hfTicketsByCustomerId({ customerId: base.orgId })
                if (!isCurrent()) return
                this.tickets = normalizeTickets(response?.data)
                this.organizationId = base.orgId
            } catch (error) {
                if (isCurrent()) this.error = 'Your support tickets could not be loaded. Please try again.'
            } finally {
                if (isCurrent()) {
                    this.activeContext = null
                    this.loading = false
                }
            }
        },
        selectTicket(id) {
            if (!this.isMember || this.loading || this.error || !this.organizationId) return
            const ticket = this.tickets.find((item) => item.id === id)
            if (!ticket) return
            this.selectedTicket = ticket
            this.dialog = true
        },
        closeDialog() {
            this.dialog = false
            this.selectedTicket = null
        }
    }
})
</script>

<style scoped>
.support-tickets-page {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    padding: 34px 0 54px;
    color: #f2f4fa;
    background: radial-gradient(ellipse at 88% 0, rgba(219, 81, 32, 0.15), transparent 38rem),
        linear-gradient(145deg, #292c35, #22252d 65%, #2a2d35);
    font-family: 'Work Sans', 'Roboto', sans-serif;
}
.support-tickets-page::before {
    position: fixed;
    z-index: 0;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    content: '';
    filter: grayscale(1) brightness(0.8);
    mix-blend-mode: soft-light;
    opacity: 0.1;
    pointer-events: none;
}
.support-tickets-page__container {
    position: relative;
    z-index: 1;
    min-width: 0;
    max-width: 1260px;
}
.support-tickets-card {
    overflow: hidden;
    color: #f1f2f5;
    background: rgba(35, 38, 46, 0.88);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
}
.support-tickets-page__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin: 0 0 20px;
    padding: 3px 2px 0;
}
.support-tickets-page__heading-copy {
    position: relative;
    padding-left: 17px;
}
.support-tickets-page__heading-copy::before {
    position: absolute;
    top: 2px;
    bottom: 3px;
    left: 0;
    width: 3px;
    background: #db5120;
    border-radius: 3px;
    content: '';
}
.support-tickets-page__eyebrow {
    color: #ff9a74;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.4px;
}
.support-tickets-page__heading h2 {
    margin: 5px 0 4px;
    color: #f4f5f8;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 600;
    letter-spacing: -0.75px;
}
.support-tickets-page__heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 12px;
    line-height: 1.5;
}
.support-tickets-page__count {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    min-height: 34px;
    padding: 0 10px;
    color: #c7cbd4;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
}
.support-tickets-card__filters {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    gap: 12px;
    padding: 14px 20px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}
.support-tickets-card__filter {
    min-width: 0;
    margin: 0;
    padding: 0;
}
.support-tickets-card__filter ::v-deep .v-input__slot {
    min-height: 40px;
    padding: 0 11px;
    background: rgba(18, 20, 26, 0.36);
    border-radius: 6px;
}
.support-tickets-card__filter ::v-deep fieldset {
    border-color: rgba(255, 255, 255, 0.13);
}
.support-tickets-card__filter ::v-deep .v-label {
    color: #aeb5c3;
    font-size: 11px;
}
.support-tickets-card__filter ::v-deep input {
    color: #f1f2f5;
    font-size: 11px;
}
.support-tickets-card__filter ::v-deep .v-icon {
    color: #aeb5c3;
}
.support-tickets-card__state {
    padding: 40px 24px;
    color: #c6c8d0;
    text-align: center;
}
.support-tickets-card__state p {
    margin: 12px 0;
}
.support-tickets-page__action {
    padding: 9px 18px;
    color: #fff;
    background: #cb481e;
    border-radius: 6px;
}
.support-tickets-card__table-wrap {
    overflow: hidden;
    padding: 0 12px 12px;
}
.support-tickets-table.theme--dark.v-data-table {
    color: #e9edf5;
    background: transparent;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.support-tickets-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
    background: #22252d;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.support-tickets-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}
.support-tickets-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.support-tickets-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.support-tickets-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
    background: #ff9a74;
}
.support-tickets-table ::v-deep table {
    width: 100%;
    min-width: 1120px;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-variant-numeric: tabular-nums;
}
.support-tickets-table ::v-deep th:nth-child(1) {
    width: 120px;
}
.support-tickets-table ::v-deep th:nth-child(2) {
    width: 32%;
}
.support-tickets-table ::v-deep th:nth-child(3) {
    width: 140px;
}
.support-tickets-table ::v-deep th:nth-child(4) {
    width: 135px;
}
.support-tickets-table ::v-deep th:nth-child(5) {
    width: 220px;
}
.support-tickets-table ::v-deep th:nth-child(6) {
    width: 115px;
}
.support-tickets-table ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    height: 46px;
    padding: 0 14px;
    color: #d6d9e1 !important;
    background: #30333b;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05px;
    text-transform: none;
    white-space: nowrap;
}
.support-tickets-table ::v-deep thead th + th {
    box-shadow: inset 1px 0 rgba(255, 255, 255, 0.045);
}
.support-tickets-table ::v-deep tbody td {
    height: 52px;
    padding: 10px 14px;
    color: #e2e5ec !important;
    background: #282b33;
    border-bottom: 1px solid rgba(255, 255, 255, 0.055) !important;
    font-size: 11px;
    line-height: 1.5;
    overflow-wrap: anywhere;
}
.support-tickets-table ::v-deep tbody td + td {
    box-shadow: inset 1px 0 rgba(255, 255, 255, 0.025);
}
.support-tickets-table ::v-deep tbody tr:nth-child(even) td {
    background: #252830;
}
.support-tickets-table ::v-deep tbody tr:hover td {
    background: rgba(219, 81, 32, 0.12) !important;
}
.support-tickets-table ::v-deep tbody tr:last-child td {
    border-bottom: 0 !important;
}
.support-tickets-table ::v-deep .v-data-footer {
    color: #aeb5c3;
    background: transparent;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 10px;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.support-tickets-table ::v-deep .v-data-footer .v-icon {
    color: #c6cbd5;
}
.support-tickets-table ::v-deep .v-data-footer__select,
.support-tickets-table ::v-deep .v-data-footer__pagination {
    color: #aeb5c3;
}
.support-tickets-table ::v-deep .v-data-footer .v-input__slot::before,
.support-tickets-table ::v-deep .v-data-footer .v-input__slot::after {
    display: none;
}
.support-tickets-table__subject {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.support-tickets-table__open {
    min-height: 30px;
    padding: 2px 3px;
    color: #ffb396;
    background: none;
    border: 0;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.1px;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
}
.support-tickets-table__open:hover {
    text-decoration: underline;
}
.support-tickets-table__email {
    display: block;
    color: #adb5c6;
    font-size: 11px;
}
.support-tickets-table__status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    width: 100%;
    max-width: 100%;
    padding: 5px 9px;
    color: #ffcfbb;
    background: rgba(219, 81, 32, 0.08);
    border: 1px solid rgba(240, 120, 76, 0.2);
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
}
.support-tickets-table__status.is-green {
    color: #b9e6c7;
    background: rgba(91, 170, 117, 0.12);
    border-color: rgba(91, 170, 117, 0.4);
}
.support-tickets-table__status.is-neutral {
    color: #d4d5dc;
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
}
.support-tickets-page button:focus-visible,
.support-tickets-page input:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 600px) {
    .support-tickets-page {
        padding-top: 20px;
    }
}
@media (max-width: 520px) {
    .support-tickets-page__heading {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 15px;
    }
    .support-tickets-card__filters {
        padding: 13px 15px;
    }
}
</style>
